import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Table } from 'react-bootstrap';
import { useNavigate, useParams } from 'react-router-dom';
import pharmacyService from '../../../services/pharmacyService';
import { usePharmacy } from '../pharmacyContext';
import { CENTRAL_PHARMACY_PATHS } from '../pharmacyPaths';
import { PHARMACY_COPY, statusLabel, CENTRAL_ROLES } from '../pharmacyCopy';
import { useAuth } from '../../../context/AuthContext';
import Icon from '../../common/Icons';
import { PharmacyPageHeader, LoadingBlock, EmptyBlock, formatDate, formatNumber } from '../ui';
import {
  DistributionFlow,
  distributionStatusClass,
  nextActionLabel
} from '../components/distributionUi';
import SmartDistributionComposer from '../components/SmartDistributionComposer';

const copy = PHARMACY_COPY.DISTRIBUTIONS;
const EDITABLE_STATUSES = ['PENDING', 'PREPARING', 'SHIPPED'];

function itemsToDraft(items) {
  return (items || []).map((it) => ({
    medication_id: String(it.medication_id),
    source_lot_id: it.source_lot_id ? String(it.source_lot_id) : '',
    lot_number: it.lot_number || '',
    expiry_date: it.expiry_date ? String(it.expiry_date).slice(0, 10) : '',
    quantity: String(it.quantity)
  }));
}

export default function PharmacyDistributionDetailPage() {
  const { distributionId } = useParams();
  const navigate = useNavigate();
  const { toast } = usePharmacy();
  const { user } = useAuth();
  const isCentral = CENTRAL_ROLES.includes(user?.role);

  const [detail, setDetail] = useState(null);
  const [draft, setDraft] = useState(null);
  const [medications, setMedications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await pharmacyService.getDistribution(distributionId);
      if (res.success) {
        setDetail(res.data);
        setDraft({
          dest_site_id: String(res.data.dest_site_id),
          notes: res.data.notes || '',
          items: itemsToDraft(res.data.items)
        });
      } else {
        setDetail(null);
        setDraft(null);
        toast('danger', res.message || 'Distribution introuvable.');
      }
    } catch (e) {
      setDetail(null);
      setDraft(null);
      toast('danger', e.message || 'Distribution introuvable.');
    } finally {
      setLoading(false);
    }
  }, [distributionId, toast]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (!isCentral || !detail || !EDITABLE_STATUSES.includes(detail.status)) return undefined;
    let cancelled = false;
    pharmacyService.listMedications({ status: 'ACTIVE' }).then((res) => {
      if (!cancelled) setMedications(res.success ? res.data || [] : []);
    });
    return () => {
      cancelled = true;
    };
  }, [isCentral, detail]);

  const canEdit = isCentral && detail && EDITABLE_STATUSES.includes(detail.status);

  const destSites = useMemo(() => {
    if (!detail) return [];
    return [{ id: detail.dest_site_id, name: detail.dest_site_name }];
  }, [detail]);

  const draftLineCount = useMemo(
    () =>
      (draft?.items || []).filter((l) => l.medication_id && l.source_lot_id && l.quantity).length,
    [draft]
  );

  const isDirty = useMemo(() => {
    if (!canEdit || !detail || !draft) return false;
    const savedItems = itemsToDraft(detail.items);
    const draftItems = (draft.items || []).filter(
      (l) => l.medication_id && l.source_lot_id && l.quantity
    );
    if (savedItems.length !== draftItems.length) return true;
    const savedKey = savedItems
      .map((l) => `${l.source_lot_id}:${l.quantity}`)
      .sort()
      .join('|');
    const draftKey = draftItems
      .map((l) => `${l.source_lot_id}:${l.quantity}`)
      .sort()
      .join('|');
    return savedKey !== draftKey;
  }, [canEdit, detail, draft]);

  const goBack = () => navigate(CENTRAL_PHARMACY_PATHS.DISTRIBUTIONS);

  const act = async (fn, okMsg) => {
    setBusy(true);
    try {
      const res = await fn();
      if (res.success) {
        toast('success', okMsg);
        if (res.data) {
          setDetail(res.data);
          setDraft({
            dest_site_id: String(res.data.dest_site_id),
            notes: res.data.notes || '',
            items: itemsToDraft(res.data.items)
          });
        } else {
          await load();
        }
      } else {
        toast('danger', res.message || 'Échec.');
      }
    } catch (e) {
      toast('danger', e.message || 'Échec.');
    } finally {
      setBusy(false);
    }
  };

  const saveItems = async () => {
    const items = (draft.items || [])
      .filter((l) => l.medication_id && l.source_lot_id && l.quantity)
      .map((l) => ({
        medication_id: l.medication_id,
        source_lot_id: l.source_lot_id,
        quantity: l.quantity
      }))
      .reverse();
    if (!items.length) {
      toast('warning', copy.LINES_HINT);
      return;
    }
    setSaving(true);
    try {
      const res = await pharmacyService.updateDistribution(detail.id, { items });
      if (res.success) {
        toast('success', copy.EDIT_OK);
        setDetail(res.data);
        setDraft({
          dest_site_id: String(res.data.dest_site_id),
          notes: res.data.notes || '',
          items: itemsToDraft(res.data.items)
        });
      } else {
        toast('danger', res.message || 'Échec.');
      }
    } catch (e) {
      toast('danger', e.message || 'Échec.');
    } finally {
      setSaving(false);
    }
  };

  const cancelDistribution = async () => {
    if (!window.confirm(copy.CONFIRM_CANCEL.replace('{number}', detail.distribution_number))) return;
    await act(
      () => pharmacyService.setDistributionStatus(detail.id, 'CANCELLED'),
      copy.CANCEL_OK
    );
  };

  if (loading) return <LoadingBlock label="Chargement de la distribution…" />;
  if (!detail || !draft) {
    return (
      <div className="cp-order-page">
        <EmptyBlock label="Distribution introuvable." />
        <button type="button" className="cp-btn cp-btn--ghost mt-3" onClick={goBack}>
          {copy.BACK_TO_LIST}
        </button>
      </div>
    );
  }

  const items = [...(detail.items || [])].sort((a, b) => Number(b.id) - Number(a.id));

  return (
    <div className="cp-order-page">
      <PharmacyPageHeader
        badge={copy.BADGE}
        title={detail.distribution_number}
        description={
          <span className="d-inline-flex flex-wrap align-items-center gap-2">
            <span className={`cp-status-pill ${distributionStatusClass(detail.status)}`}>
              {statusLabel(detail.status)}
            </span>
            <span className="text-muted">{nextActionLabel(detail.status)}</span>
            {canEdit ? (
              <span className="cp-status-pill cp-status-pill--info">{copy.EDITABLE_BADGE}</span>
            ) : null}
          </span>
        }
        actions={
          <button type="button" className="cp-btn cp-btn--ghost" onClick={goBack}>
            <Icon name="chevronLeft" size={16} aria-hidden />
            {copy.BACK_TO_LIST}
          </button>
        }
      />

      <DistributionFlow status={detail.status} />

      <div className="cp-order-meta">
        <div>
          <span className="cp-order-meta__label">{copy.DETAIL_SOURCE}</span>
          <strong>{detail.source_site_name || '—'}</strong>
        </div>
        <div>
          <span className="cp-order-meta__label">{copy.DETAIL_DEST}</span>
          <strong>{detail.dest_site_name || '—'}</strong>
        </div>
        <div>
          <span className="cp-order-meta__label">{copy.DETAIL_CREATED}</span>
          <strong>{detail.created_at ? formatDate(detail.created_at) : '—'}</strong>
        </div>
        {detail.delivered_at ? (
          <div>
            <span className="cp-order-meta__label">{copy.DETAIL_DELIVERED}</span>
            <strong>{formatDate(detail.delivered_at)}</strong>
          </div>
        ) : null}
      </div>

      {detail.notes ? (
        <div className="cp-order-notes">
          <span className="cp-order-meta__label">{copy.DETAIL_NOTES}</span>
          <p className="mb-0">{detail.notes}</p>
        </div>
      ) : null}

      <section className="cp-form-section">
        <div className="cp-form-section__head">
          <h3 className="cp-form-section__title mb-0">{copy.SECTION_ITEMS}</h3>
          {canEdit ? (
            <span className="cp-form-section__meta text-muted">{copy.EDIT_HINT}</span>
          ) : null}
        </div>

        {canEdit ? (
          <>
            <SmartDistributionComposer
              medications={medications}
              destSites={destSites}
              centralSiteId={detail.source_site_id}
              value={draft}
              onChange={setDraft}
              copy={copy}
              linesOnly
            />
            <div className="cp-order-page__actions cp-order-page__actions--inline">
              <div className="cp-order-page__actions-meta">
                <strong>{draftLineCount}</strong>
                <span>{copy.RESULTS_LINES}</span>
                {isDirty ? (
                  <span className="cp-order-page__actions-dirty">{copy.EDIT_UNSAVED}</span>
                ) : null}
              </div>
              <div className="cp-order-page__actions-btns">
                <button
                  type="button"
                  className="cp-btn cp-btn--primary"
                  disabled={saving || busy || draftLineCount === 0 || !isDirty}
                  onClick={saveItems}
                >
                  {saving ? copy.SAVING : copy.EDIT_SAVE}
                </button>
                {isDirty ? (
                  <button
                    type="button"
                    className="cp-btn cp-btn--ghost"
                    disabled={saving || busy}
                    onClick={() =>
                      setDraft({
                        dest_site_id: String(detail.dest_site_id),
                        notes: detail.notes || '',
                        items: itemsToDraft(detail.items)
                      })
                    }
                  >
                    {copy.EDIT_RESET}
                  </button>
                ) : null}
              </div>
            </div>
          </>
        ) : (
          <div className="cp-table-wrap">
            <Table responsive className="cp-table mb-0">
              <thead>
                <tr>
                  <th style={{ width: 40 }}>#</th>
                  <th>{copy.COL_MED}</th>
                  <th className="text-end">{copy.COL_QTY}</th>
                  <th>{copy.COL_LOT}</th>
                  <th>{copy.COL_EXPIRY}</th>
                </tr>
              </thead>
              <tbody>
                {items.map((it, displayIdx) => {
                  const rowNum = items.length - displayIdx;
                  return (
                    <tr key={it.id}>
                      <td className="cp-num text-muted">{rowNum}</td>
                      <td>
                        <strong>{it.medication_name}</strong>
                        {it.unit_name ? (
                          <span className="text-muted small ms-2">{it.unit_name}</span>
                        ) : null}
                      </td>
                      <td className="text-end cp-num">{formatNumber(it.quantity)}</td>
                      <td>{it.lot_number || '—'}</td>
                      <td>{it.expiry_date ? formatDate(it.expiry_date) : '—'}</td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          </div>
        )}
      </section>

      {isCentral && detail.status !== 'DELIVERED' && detail.status !== 'CANCELLED' ? (
        <div className="cp-order-page__actions">
          <div className="cp-order-page__actions-meta">
            <span className="text-muted">{copy.ACTIONS_HINT}</span>
          </div>
          <div className="cp-order-page__actions-btns">
            {detail.status === 'PENDING' ? (
              <button
                type="button"
                className="cp-btn cp-btn--ghost"
                disabled={busy || saving}
                onClick={() =>
                  act(
                    () => pharmacyService.setDistributionStatus(detail.id, 'PREPARING'),
                    copy.ACT_PREPARE_OK
                  )
                }
              >
                {copy.ACT_PREPARE}
              </button>
            ) : null}
            {detail.status === 'PREPARING' ? (
              <button
                type="button"
                className="cp-btn cp-btn--ghost"
                disabled={busy || saving}
                onClick={() =>
                  act(
                    () => pharmacyService.setDistributionStatus(detail.id, 'SHIPPED'),
                    copy.ACT_SHIP_OK
                  )
                }
              >
                {copy.ACT_SHIP}
              </button>
            ) : null}
            {['PREPARING', 'SHIPPED'].includes(detail.status) ? (
              <button
                type="button"
                className="cp-btn cp-btn--primary"
                disabled={busy || saving || isDirty}
                onClick={() =>
                  act(
                    () => pharmacyService.deliverDistribution(detail.id),
                    copy.ACT_DELIVER_OK
                  )
                }
              >
                {copy.ACT_DELIVER}
              </button>
            ) : null}
            {EDITABLE_STATUSES.includes(detail.status) ? (
              <button
                type="button"
                className="cp-btn cp-btn--danger"
                disabled={busy || saving}
                onClick={cancelDistribution}
              >
                {copy.ACT_CANCEL}
              </button>
            ) : null}
          </div>
        </div>
      ) : null}
    </div>
  );
}
